import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { t } from "i18next";

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { MuiButton } from "../../../../components/mui";
import ReactTableBuilder from "../../../../utils/reactTableBuilder";
import shipmentApi from "../../../../api/shipmentApi";


/**
 * Lists the shipments still in Draft and lets the user load one into the
 * shipping screen. Selection is handed back to the parent via onSelect —
 * this component never loads the shipment itself.
 *
 * originLocationId (optional): when supplied, the list is restricted to
 * shipments whose origineLocationId matches it — e.g. the CM Pallet screen
 * only wants to show shipments that originate from the current user's own
 * plant. Omitted, the dialog behaves exactly as before (every Draft
 * shipment).
 */
function ShipmentListDialog({ isOpen, onClose, onSelect, plants = [], destinations = [], originLocationId = null, ...props }) {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Maps a NetSuite location ID to the plant name for display.
  const originNameById = {};
  plants.forEach(p => {
    if (p.netsuiteCmId) originNameById[p.netsuiteCmId] = p.name;
  });

  const destinationNameById = {};
  destinations.forEach(d => { destinationNameById[String(d.id)] = d.name; });

  // Refetch every time the dialog opens so the list is never stale.
  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    setError("");

    shipmentApi.getShipmentsByStatus("Draft")
      .then(res => {
        const all = res.data ?? [];
        const filtered = originLocationId != null
          ? all.filter(s => String(s.origineLocationId) === String(originLocationId))
          : all;
        setShipments(filtered);
      })
      .catch(err => {
        console.error("Failed to load draft shipments", err);
        setError(t("shipping_cm.shipment_list_load_error"));
      })
      .finally(() => setLoading(false));
  }, [isOpen, originLocationId]);

  function handleSelect(row) {
    onSelect(row.shipmentCode);
  }

  const tableColumns = [
    {
      Header: t("shipping_cm.shipment_id"),
      accessor: "shipmentCode",
    },
    {
      Header: t("shipping_cm.origin_cm"),
      accessor: (row) => originNameById[row.origineLocationId] ?? row.origineLocationId ?? "",
    },
    {
      Header: t("shipping_cm.destination"),
      accessor: (row) => destinationNameById[String(row.destination)] ?? row.destination ?? "",
    },
    {
      Header: t("shipping_cm.shipment_id_logistica"),
      accessor: "shipmentIdLogistica",
    },
    {
      Header: t("shipping_cm.load"),
      accessor: (row) => (
        <MuiButton
          color="primary"
          onClick={() => handleSelect(row)}
        >
          {t("shipping_cm.load")}
        </MuiButton>
      ),
    },
  ];

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} disableEnforceFocus fullWidth maxWidth="md">
        <DialogTitle>{t("shipping_cm.shipment_list_title")}</DialogTitle>
        <DialogContent>
          {loading && <p>{t("loading")}</p>}
          {!loading && error && <p>{error}</p>}
          {!loading && !error && shipments.length === 0 && (
            <p>{t("shipping_cm.no_draft_shipments")}</p>
          )}
          {!loading && !error && shipments.length > 0 && (
            <ReactTableBuilder columns={tableColumns} data={shipments} maxRows={10} />
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton color="primary" onClick={onClose}>{t("close")}</MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

ShipmentListDialog.propTypes = {
  auth: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  auth: state.auth,
  errors: state.errors
});

export default compose(
  withTranslation(),
  connect(
    mapStateToProps,
  )
)(ShipmentListDialog);